<script setup>
// AGENTS-LINK-01 (L-08): o link de teste do agente. Uma página pública onde qualquer pessoa
// conversa com a versão PUBLICADA do agente, sem login e sem caixa de entrada.
//
// Mora dentro da seção de caixas, que já é só do super admin: gerar, configurar e revogar o link
// são atos de plataforma, e o backend recusa o resto (`TestLinksController`).
//
// O `show` junta o que é do OS (o link, a configuração, o modo do Playground) com o que é do
// runtime (as ferramentas publicadas e o gasto do dia). Runtime fora do ar não derruba o bloco: a
// parte que depende dele vira texto no lugar da lista ou do gasto, e o resto segue.
import { ref, reactive, computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import powerAgentsApi from 'dashboard/api/powerAgents';
import teamsApi from 'dashboard/api/teams';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { mensagemDoErro } from '../erros';
import RastroDoTurno from './RastroDoTurno.vue';

const { t, locale } = useI18n();
const ctx = inject('powerAgentContext');

// `pt_BR` do i18n vira `pt-BR`, que é o que o Intl entende.
const idioma = computed(() => String(locale.value || 'en').replace('_', '-'));

const carregando = ref(true);
const erroAoCarregar = ref('');
const link = ref(null);
const playgroundMode = ref('motor');
const ferramentas = ref({ error: null, itens: [] });
const gasto = ref({ error: null });
const sessoes = ref({ total: 0, sessoes: [] });
const erroDasSessoes = ref('');

const gerando = ref(false);
const revogando = ref(false);
const salvando = ref(false);
const trocandoModo = ref('');
const copiado = ref(false);
const qr = ref('');
// A confirmação mora na própria tela, e não num `confirm()`: 'gerar', 'gerar_outro' ou 'salvar'.
const pendente = ref(null);

// ── Erros do runtime ──────────────────────────────────────────────
// Viram texto no lugar da lista ou do gasto. O código cru só aparece quando não é um dos nomeados.
function textoDoErro(codigo) {
  if (codigo === 'runtime_inalcancavel') {
    return t('POWER_AGENTS.TEST_LINK.RUNTIME_UNREACHABLE');
  }
  if (codigo === 'agente_nao_publicado') {
    return t('POWER_AGENTS.TEST_LINK.NOT_PUBLISHED');
  }
  if (codigo === 'runtime_resposta_invalida') {
    return t('POWER_AGENTS.TEST_LINK.RUNTIME_INVALID');
  }
  const status = /^runtime_respondeu_(\d+)$/.exec(codigo || '');
  if (status) {
    return t('POWER_AGENTS.TEST_LINK.RUNTIME_STATUS', { status: status[1] });
  }
  return codigo;
}

// ── Ferramentas e o aviso de acesso irrestrito ────────────────────
// O nome de função é contrato com o modelo (`fechar_chamado`); na tela vira texto, como na aba
// Ferramentas. Sem nome, vale a descrição.
function nomeLegivel(ferramenta) {
  const nome = String(ferramenta.nome || '')
    .replace(/[_-]+/g, ' ')
    .trim();
  return nome || ferramenta.descricao || '';
}

const listas = computed(() => {
  const itens = ferramentas.value.itens || [];
  return [
    {
      id: 'grava',
      titulo: t('POWER_AGENTS.TEST_LINK.TOOLS_WRITE'),
      itens: itens.filter(f => f.grava),
    },
    {
      id: 'consulta',
      titulo: t('POWER_AGENTS.TEST_LINK.TOOLS_READ'),
      itens: itens.filter(f => f.consulta_sistema),
    },
  ];
});

const naoPublicado = computed(
  () => ferramentas.value.error === 'agente_nao_publicado'
);
// Sem a lista (runtime fora, resposta que não se lê) não dá para dizer que nada grava. O aviso
// sai do mesmo jeito, dizendo que não deu para saber: calar seria afirmar que está tudo certo.
const ferramentasDesconhecidas = computed(
  () => Boolean(ferramentas.value.error) && !naoPublicado.value
);
// Uma ferramenta pode estar nas duas listas; no aviso ela entra uma vez.
const sensiveis = computed(() => [
  ...new Set(listas.value.flatMap(lista => lista.itens)),
]);
const precisaAviso = computed(
  () => sensiveis.value.length > 0 || ferramentasDesconhecidas.value
);

const fraseDoAviso = computed(() => {
  if (ferramentasDesconhecidas.value) {
    return t('POWER_AGENTS.TEST_LINK.WARNING_UNKNOWN');
  }
  const nomes = [...new Set(sensiveis.value.map(nomeLegivel))];
  const acoes = new Intl.ListFormat(idioma.value, {
    style: 'long',
    type: 'conjunction',
  }).format(nomes);
  return t('POWER_AGENTS.TEST_LINK.WARNING', { acoes });
});

// O acesso com que o próximo link nasce: gerar outro herda o do atual; sem link ativo, o novo
// nasce com o padrão, que é irrestrito.
const acessoDoProximo = computed(() => link.value?.acesso || 'irrestrito');
const avisoAoGerar = computed(
  () => acessoDoProximo.value === 'irrestrito' && precisaAviso.value
);

// ── Datas ─────────────────────────────────────────────────────────
// O campo de validade é de DIA, no fuso de quem edita; a volta é o fim daquele dia, no mesmo fuso.
const pad = n => String(n).padStart(2, '0');

function paraDia(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fimDoDia(dia) {
  if (!dia) return null;
  const d = new Date(`${dia}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const HOJE = paraDia(new Date().toISOString());

function quando(iso) {
  return new Date(iso).toLocaleString(idioma.value, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const validadeDoLink = computed(() => {
  const atual = link.value;
  if (!atual?.expira_em) return t('POWER_AGENTS.TEST_LINK.NO_EXPIRY');
  const vence = quando(atual.expira_em);
  return atual.expirado
    ? t('POWER_AGENTS.TEST_LINK.EXPIRED_AT', { quando: vence })
    : t('POWER_AGENTS.TEST_LINK.EXPIRES_AT', { quando: vence });
});

// ── Carregar ──────────────────────────────────────────────────────
async function carregarSessoes() {
  erroDasSessoes.value = '';
  try {
    sessoes.value = await powerAgentsApi.testLinkSessions(
      ctx.accountId(),
      ctx.agentId()
    );
  } catch (e) {
    erroDasSessoes.value = mensagemDoErro(
      e,
      t,
      t('POWER_AGENTS.TEST_LINK.SESSIONS_ERROR')
    );
  }
}

async function carregar() {
  carregando.value = true;
  erroAoCarregar.value = '';
  try {
    const dados = await powerAgentsApi.testLink(ctx.accountId(), ctx.agentId());
    link.value = dados.link;
    playgroundMode.value = dados.playground_mode;
    ferramentas.value = dados.ferramentas;
    gasto.value = dados.gasto;
  } catch (e) {
    erroAoCarregar.value = mensagemDoErro(
      e,
      t,
      t('POWER_AGENTS.TEST_LINK.LOAD_ERROR')
    );
  } finally {
    carregando.value = false;
  }
}

// ── O link ────────────────────────────────────────────────────────
async function gerar() {
  pendente.value = null;
  gerando.value = true;
  try {
    const resp = await powerAgentsApi.testLinkCreate(
      ctx.accountId(),
      ctx.agentId()
    );
    link.value = resp.link;
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    gerando.value = false;
  }
}

// Acesso irrestrito com ferramenta que grava ou consulta o sistema do cliente: a frase aparece
// ANTES de o link existir, e ele só nasce com a confirmação.
function pedirGerar() {
  if (avisoAoGerar.value) {
    pendente.value = 'gerar';
    return;
  }
  gerar();
}

async function revogar() {
  pendente.value = null;
  revogando.value = true;
  try {
    const resp = await powerAgentsApi.testLinkRevoke(
      ctx.accountId(),
      ctx.agentId()
    );
    link.value = resp.link;
    useAlert(t('POWER_AGENTS.TEST_LINK.REVOKED'));
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    revogando.value = false;
  }
}

async function copiar() {
  try {
    await copyTextToClipboard(link.value.url);
    copiado.value = true;
    setTimeout(() => {
      copiado.value = false;
    }, 2000);
  } catch (e) {
    useAlert(t('POWER_AGENTS.TEST_LINK.COPY_FAILED'));
  }
}

// Sem o QR o endereço continua ali para copiar, então a falha dele não vira alerta. A conferência
// do endereço no fim descarta o QR de um link que já foi trocado enquanto este era desenhado.
watch(
  () => link.value?.url,
  async url => {
    qr.value = '';
    if (!url) return;
    try {
      const imagem = await QRCode.toDataURL(url, { width: 176, margin: 1 });
      if (link.value?.url === url) qr.value = imagem;
    } catch (e) {
      qr.value = '';
    }
  }
);

// ── Modos ─────────────────────────────────────────────────────────
// Rótulo e frase saem prontos daqui, e não de `t()` com chave montada: chave montada some do
// inventário de i18n sem ninguém notar.
const MODOS = ['motor', 'canal'];

function rotuloDoModo(modo) {
  return modo === 'canal'
    ? t('POWER_AGENTS.TEST_LINK.MODE_CANAL')
    : t('POWER_AGENTS.TEST_LINK.MODE_MOTOR');
}

function fraseDoModo(modo) {
  return modo === 'canal'
    ? t('POWER_AGENTS.TEST_LINK.MODE_CANAL_HINT')
    : t('POWER_AGENTS.TEST_LINK.MODE_MOTOR_HINT');
}

async function trocarModoDoLink(modo) {
  if (link.value.modo === modo) return;
  trocandoModo.value = 'link';
  try {
    const resp = await powerAgentsApi.testLinkUpdate(
      ctx.accountId(),
      ctx.agentId(),
      { modo }
    );
    link.value = resp.link;
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    trocandoModo.value = '';
  }
}

// O modo do Playground é do agente, e não do link: existe com ou sem link, e trocar não pede
// publicação. A página fica sabendo, e o Playground passa a conversar no modo novo (L-27).
async function trocarModoDoPlayground(modo) {
  if (playgroundMode.value === modo) return;
  trocandoModo.value = 'playground';
  try {
    const resp = await powerAgentsApi.playgroundModeUpdate(
      ctx.accountId(),
      ctx.agentId(),
      modo
    );
    playgroundMode.value = resp.playground_mode;
    ctx.modoDoPlaygroundMudou(resp.playground_mode);
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    trocandoModo.value = '';
  }
}

// O mesmo seletor para os dois modos; o do link só existe quando há link.
const seletoresDeModo = computed(() => [
  ...(link.value
    ? [
        {
          id: 'link',
          titulo: t('POWER_AGENTS.TEST_LINK.LINK_MODE'),
          valor: link.value.modo,
          trocar: trocarModoDoLink,
        },
      ]
    : []),
  {
    id: 'playground',
    titulo: t('POWER_AGENTS.TEST_LINK.PLAYGROUND_MODE'),
    valor: playgroundMode.value,
    trocar: trocarModoDoPlayground,
  },
]);

// ── Configuração do link ──────────────────────────────────────────
const opcoesDeAcesso = computed(() => [
  { id: 'irrestrito', rotulo: t('POWER_AGENTS.TEST_LINK.ACCESS_OPEN') },
  { id: 'senha', rotulo: t('POWER_AGENTS.TEST_LINK.ACCESS_PASSWORD') },
  { id: 'permitidos', rotulo: t('POWER_AGENTS.TEST_LINK.ACCESS_ALLOWED') },
]);

const rascunho = reactive({
  acesso: 'irrestrito',
  senha: '',
  emails: '',
  membros: false,
  validade: '',
  limite: '',
  perguntas: '',
  oQueTestar: '',
});

function preencherRascunho(dados) {
  Object.assign(rascunho, {
    acesso: dados.acesso,
    senha: '',
    emails: (dados.emails_permitidos || []).join('\n'),
    membros: Boolean(dados.membros_permitidos),
    validade: paraDia(dados.expira_em),
    limite: dados.limite_por_visitante ?? '',
    perguntas: (dados.perguntas_sugeridas || []).join('\n'),
    oQueTestar: dados.o_que_testar || '',
  });
}

// O rascunho segue o link quando ele TROCA (carregou, gerou outro), e não a cada resposta: trocar
// o modo devolve o link inteiro, e refazer o rascunho ali apagaria o que a pessoa estava digitando.
watch(
  () => link.value?.id,
  id => {
    if (id) preencherRascunho(link.value);
  }
);

// Trocar o acesso depois de a confirmação aparecer a deixa falando de outra escolha.
watch(
  () => rascunho.acesso,
  () => {
    if (pendente.value === 'salvar') pendente.value = null;
  }
);

function linhas(texto, separador) {
  return String(texto || '')
    .split(separador)
    .map(parte => parte.trim())
    .filter(Boolean);
}

function corpoDoSalvar() {
  const corpo = {
    acesso: rascunho.acesso,
    emails_permitidos: linhas(rascunho.emails, /[\s,;]+/),
    membros_permitidos: rascunho.membros,
    // O campo de número devolve '' quando vazio, e vazio é sem limite.
    limite_por_visitante:
      rascunho.limite === '' ? null : Number(rascunho.limite),
    perguntas_sugeridas: linhas(rascunho.perguntas, '\n'),
    o_que_testar: rascunho.oQueTestar.trim() || null,
  };
  // A senha gravada nunca volta do servidor; só vai a que foi digitada agora.
  if (rascunho.senha) corpo.senha = rascunho.senha;
  // A validade só vai quando mudou. O campo é de dia, e reenviar o que já estava empurraria o
  // vencimento para o fim do dia a cada save.
  if (rascunho.validade !== paraDia(link.value.expira_em)) {
    corpo.expira_em = fimDoDia(rascunho.validade);
  }
  return corpo;
}

async function salvar() {
  pendente.value = null;
  salvando.value = true;
  try {
    const resp = await powerAgentsApi.testLinkUpdate(
      ctx.accountId(),
      ctx.agentId(),
      corpoDoSalvar()
    );
    link.value = resp.link;
    preencherRascunho(resp.link);
    useAlert(t('POWER_AGENTS.TEST_LINK.SAVED'));
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    salvando.value = false;
  }
}

// Abrir um link que tinha senha ou lista para quem tiver o endereço é o mesmo risco de gerá-lo
// aberto: a frase aparece, e o save só sai com a confirmação.
function pedirSalvar() {
  const abrindo =
    rascunho.acesso === 'irrestrito' && link.value.acesso !== 'irrestrito';
  if (abrindo && precisaAviso.value) {
    pendente.value = 'salvar';
    return;
  }
  salvar();
}

// ── Gasto, contadores e sessões ───────────────────────────────────
// O valor no formato do idioma da tela: `0,01` em português, `0.01` em inglês (L-21).
function dinheiro(valor) {
  if (valor === null || valor === undefined) return '—';
  return new Intl.NumberFormat(idioma.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(valor));
}

const contadores = computed(() => {
  const lista = sessoes.value.sessoes || [];
  const soma = campo =>
    lista.reduce((total, sessao) => total + (Number(sessao[campo]) || 0), 0);
  return [
    {
      id: 'sessoes',
      valor: sessoes.value.total || 0,
      rotulo: t('POWER_AGENTS.TEST_LINK.COUNT_SESSIONS'),
    },
    {
      id: 'mensagens',
      valor: soma('mensagens'),
      rotulo: t('POWER_AGENTS.TEST_LINK.COUNT_MESSAGES'),
    },
    // L-19: a foto e o PDF que os visitantes mandaram.
    {
      id: 'arquivos',
      valor: soma('arquivos'),
      rotulo: t('POWER_AGENTS.TEST_LINK.COUNT_FILES'),
    },
  ];
});

// A lista vem limitada (as mais recentes), e o total não. Quando cortou, as somas são só das que
// vieram — e a tela diz isso em vez de apresentar a soma parcial como o todo.
const contagemParcial = computed(
  () => (sessoes.value.total || 0) > (sessoes.value.sessoes || []).length
);

function quemEntrou(sessao) {
  if (sessao.email) return sessao.email;
  if (sessao.tipo === 'senha') {
    return t('POWER_AGENTS.TEST_LINK.SESSION_PASSWORD');
  }
  if (sessao.tipo === 'membro') {
    return t('POWER_AGENTS.TEST_LINK.SESSION_MEMBER');
  }
  return t('POWER_AGENTS.TEST_LINK.SESSION_ANONYMOUS');
}

const sessaoAberta = ref(null);
const turnos = ref([]);
const carregandoTurnos = ref(false);

// ── O rastro de cada turno (L-21) ─────────────────────────────────
// O mesmo cartão do Playground (`RastroDoTurno`), sobre a projeção que o turno guardou: as
// ferramentas que rodaram e o desfecho, a passagem a uma pessoa (que no link é só mostrada), o funil
// e o que o agente fez com cada arquivo.
const times = ref(null);

async function carregarTimes() {
  if (times.value) return;
  try {
    const { data } = await teamsApi.get();
    times.value = data || [];
  } catch (e) {
    times.value = [];
  }
}

function nomeDoTime(id) {
  return (times.value || []).find(time => time.id === id)?.name || `#${id}`;
}

const cartaoDoRastro = turno => ({
  ...turno.rastro.turn,
  id: turno.request_id || turno.id,
  handoff: turno.rastro.handoff,
  maintenance: turno.rastro.maintenance,
  attachments: turno.rastro.attachments,
});

async function alternarSessao(sessao) {
  if (sessaoAberta.value === sessao.id) {
    sessaoAberta.value = null;
    return;
  }
  carregarTimes();
  sessaoAberta.value = sessao.id;
  turnos.value = [];
  carregandoTurnos.value = true;
  try {
    const resp = await powerAgentsApi.testLinkTurns(
      ctx.accountId(),
      ctx.agentId(),
      sessao.id
    );
    if (sessaoAberta.value === sessao.id) turnos.value = resp.turnos || [];
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TEST_LINK.ERROR')));
  } finally {
    carregandoTurnos.value = false;
  }
}

onMounted(() => {
  carregar();
  carregarSessoes();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div>
      <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
        {{ t('POWER_AGENTS.TEST_LINK.TITLE') }}
      </p>
      <p class="mt-0.5 text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.TEST_LINK.DESCRIPTION') }}
      </p>
    </div>

    <p v-if="carregando" class="text-xs text-n-slate-10">
      {{ t('POWER_AGENTS.TEST_LINK.LOADING') }}
    </p>

    <p v-else-if="erroAoCarregar" class="text-xs text-n-ruby-11">
      {{ erroAoCarregar }}
    </p>

    <template v-else>
      <!-- O link: gerar, copiar, QR, gerar outro e revogar. -->
      <div
        class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div v-if="link" class="flex flex-col gap-4 sm:flex-row">
          <img
            v-if="qr"
            :src="qr"
            :alt="t('POWER_AGENTS.TEST_LINK.QR_ALT')"
            class="self-start rounded-lg size-36 shrink-0 outline outline-1 outline-n-weak"
          />
          <div class="flex flex-col flex-1 min-w-0 gap-2">
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="
                  link.ativo
                    ? 'bg-n-teal-9/10 text-n-teal-11'
                    : 'bg-n-amber-9/10 text-n-amber-11'
                "
              >
                {{
                  link.ativo
                    ? t('POWER_AGENTS.TEST_LINK.STATUS_ACTIVE')
                    : t('POWER_AGENTS.TEST_LINK.STATUS_EXPIRED')
                }}
              </span>
              <span class="text-xs text-n-slate-10">{{ validadeDoLink }}</span>
            </div>
            <code
              class="p-2 font-mono text-xs break-all rounded-lg bg-n-background text-n-slate-12"
            >
              {{ link.url }}
            </code>
            <div class="flex flex-wrap gap-2">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                :icon="copiado ? 'i-lucide-check' : 'i-lucide-copy'"
                :label="
                  copiado
                    ? t('POWER_AGENTS.TEST_LINK.COPIED')
                    : t('POWER_AGENTS.TEST_LINK.COPY')
                "
                @click="copiar"
              />
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-refresh-cw"
                :label="t('POWER_AGENTS.TEST_LINK.REGENERATE')"
                :disabled="gerando || revogando"
                @click="pendente = 'gerar_outro'"
              />
              <Button
                variant="outline"
                color="ruby"
                size="sm"
                icon="i-lucide-link-2-off"
                :label="t('POWER_AGENTS.TEST_LINK.REVOKE')"
                :is-loading="revogando"
                :disabled="gerando"
                @click="revogar"
              />
            </div>
          </div>
        </div>

        <div v-else class="flex flex-col items-start gap-2">
          <p class="text-sm text-n-slate-11">
            {{ t('POWER_AGENTS.TEST_LINK.OFF') }}
          </p>
          <Button
            size="sm"
            icon="i-lucide-link"
            :label="t('POWER_AGENTS.TEST_LINK.GENERATE')"
            :is-loading="gerando"
            :disabled="naoPublicado"
            @click="pedirGerar"
          />
        </div>

        <!-- Gerar outro tira do ar o endereço que as pessoas têm; gerar aberto com ferramenta
             que grava ou consulta o sistema do cliente expõe isso a quem tiver o endereço. As
             duas coisas se confirmam aqui, com a frase na frente do botão. -->
        <div
          v-if="pendente === 'gerar' || pendente === 'gerar_outro'"
          class="flex flex-col gap-3 px-3 py-2.5 border rounded-lg border-n-amber-6 bg-n-amber-2"
        >
          <div class="flex gap-2">
            <i
              class="mt-0.5 text-base shrink-0 i-lucide-triangle-alert text-n-amber-10"
            />
            <div class="flex flex-col gap-1 text-xs text-n-amber-11">
              <p v-if="pendente === 'gerar_outro'">
                {{ t('POWER_AGENTS.TEST_LINK.REGENERATE_CONFIRM') }}
              </p>
              <template v-if="avisoAoGerar">
                <p class="font-medium">{{ fraseDoAviso }}</p>
                <p>{{ t('POWER_AGENTS.TEST_LINK.WARNING_OPEN') }}</p>
              </template>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              amber
              sm
              :label="
                pendente === 'gerar_outro'
                  ? t('POWER_AGENTS.TEST_LINK.REGENERATE')
                  : t('POWER_AGENTS.TEST_LINK.CONFIRM_ANYWAY')
              "
              :is-loading="gerando"
              @click="gerar"
            />
            <Button
              slate
              link
              sm
              :label="t('POWER_AGENTS.TEST_LINK.CANCEL')"
              @click="pendente = null"
            />
          </div>
        </div>
      </div>

      <!-- O que o visitante aciona: as ferramentas da versão publicada que gravam dados ou
           consultam o sistema do cliente. É o que decide se o acesso irrestrito é um risco. -->
      <div
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TEST_LINK.TOOLS') }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.TEST_LINK.TOOLS_HINT') }}
          </p>
        </div>
        <p v-if="ferramentas.error" class="text-xs text-n-amber-11">
          {{ textoDoErro(ferramentas.error) }}
        </p>
        <div v-else class="grid gap-3 sm:grid-cols-2">
          <div
            v-for="lista in listas"
            :key="lista.id"
            class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-background"
          >
            <p
              class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ lista.titulo }}
            </p>
            <p v-if="!lista.itens.length" class="text-xs text-n-slate-10">
              {{ t('POWER_AGENTS.TEST_LINK.TOOLS_NONE') }}
            </p>
            <div
              v-for="ferramenta in lista.itens"
              :key="ferramenta.nome"
              class="flex flex-col min-w-0"
            >
              <span
                class="block text-sm truncate text-n-slate-12 first-letter:uppercase"
              >
                {{ nomeLegivel(ferramenta) }}
              </span>
              <span
                v-if="ferramenta.descricao"
                class="text-xs text-n-slate-10 line-clamp-2"
              >
                {{ ferramenta.descricao }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Os dois modos, pelo mesmo seletor. -->
      <div
        class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div
          v-for="seletor in seletoresDeModo"
          :key="seletor.id"
          class="flex flex-col gap-2"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ seletor.titulo }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="modo in MODOS"
              :key="modo"
              type="button"
              :data-modo="`${seletor.id}:${modo}`"
              :disabled="Boolean(trocandoModo)"
              class="px-3 py-1.5 text-xs border border-solid rounded-lg disabled:opacity-60"
              :class="
                seletor.valor === modo
                  ? 'border-n-brand text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="seletor.trocar(modo)"
            >
              {{ rotuloDoModo(modo) }}
            </button>
          </div>
          <p class="text-xs text-n-slate-10">
            {{ fraseDoModo(seletor.valor) }}
          </p>
        </div>
      </div>

      <!-- Quem entra, por quanto tempo, quanto cada um fala e o que ele vê ao abrir. -->
      <div
        v-if="link"
        class="flex flex-col gap-5 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TEST_LINK.ACCESS') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="acesso in opcoesDeAcesso"
              :key="acesso.id"
              type="button"
              :data-acesso="acesso.id"
              class="px-3 py-1.5 text-xs border border-solid rounded-lg"
              :class="
                rascunho.acesso === acesso.id
                  ? 'border-n-brand text-n-slate-12'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="rascunho.acesso = acesso.id"
            >
              {{ acesso.rotulo }}
            </button>
          </div>

          <!-- `new-password`: sem ele o gerenciador de senhas preenche o login salvo aqui. -->
          <Input
            v-if="rascunho.acesso === 'senha'"
            v-model="rascunho.senha"
            type="password"
            autocomplete="new-password"
            class="sm:w-80"
            :placeholder="
              link.senha_definida
                ? t('POWER_AGENTS.TEST_LINK.PASSWORD_SET')
                : t('POWER_AGENTS.TEST_LINK.PASSWORD_PLACEHOLDER')
            "
          />

          <template v-if="rascunho.acesso === 'permitidos'">
            <TextArea
              v-model="rascunho.emails"
              auto-height
              :placeholder="t('POWER_AGENTS.TEST_LINK.EMAILS_PLACEHOLDER')"
              :message="t('POWER_AGENTS.TEST_LINK.EMAILS_HINT')"
            />
            <div class="flex items-start gap-3 p-1">
              <Switch v-model="rascunho.membros" class="mt-0.5" />
              <div class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">
                  {{ t('POWER_AGENTS.TEST_LINK.MEMBERS') }}
                </span>
                <span class="block mt-0.5 text-xs text-n-slate-10">
                  {{ t('POWER_AGENTS.TEST_LINK.MEMBERS_HINT') }}
                </span>
              </div>
            </div>
          </template>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <!-- Sem data é sem validade, e a frase embaixo diz isso em vez de deixar o campo vazio
               parecer esquecimento. -->
          <Input
            v-model="rascunho.validade"
            type="date"
            :min="HOJE"
            :label="t('POWER_AGENTS.TEST_LINK.VALIDITY')"
            :message="
              rascunho.validade
                ? t('POWER_AGENTS.TEST_LINK.VALIDITY_HINT')
                : t('POWER_AGENTS.TEST_LINK.NO_EXPIRY')
            "
          />
          <Input
            v-model="rascunho.limite"
            type="number"
            :label="t('POWER_AGENTS.TEST_LINK.LIMIT')"
            :message="t('POWER_AGENTS.TEST_LINK.LIMIT_HINT')"
          />
        </div>

        <TextArea
          v-model="rascunho.perguntas"
          auto-height
          :label="t('POWER_AGENTS.TEST_LINK.QUESTIONS')"
          :message="t('POWER_AGENTS.TEST_LINK.QUESTIONS_HINT')"
        />

        <TextArea
          v-model="rascunho.oQueTestar"
          auto-height
          :label="t('POWER_AGENTS.TEST_LINK.WHAT_TO_TEST')"
          :placeholder="t('POWER_AGENTS.TEST_LINK.WHAT_TO_TEST_PLACEHOLDER')"
        />

        <div
          v-if="pendente === 'salvar'"
          class="flex flex-col gap-3 px-3 py-2.5 border rounded-lg border-n-amber-6 bg-n-amber-2"
        >
          <div class="flex gap-2">
            <i
              class="mt-0.5 text-base shrink-0 i-lucide-triangle-alert text-n-amber-10"
            />
            <div class="flex flex-col gap-1 text-xs text-n-amber-11">
              <p class="font-medium">{{ fraseDoAviso }}</p>
              <p>{{ t('POWER_AGENTS.TEST_LINK.WARNING_OPEN') }}</p>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              amber
              sm
              :label="t('POWER_AGENTS.TEST_LINK.SAVE_ANYWAY')"
              :is-loading="salvando"
              @click="salvar"
            />
            <Button
              slate
              link
              sm
              :label="t('POWER_AGENTS.TEST_LINK.CANCEL')"
              @click="pendente = null"
            />
          </div>
        </div>

        <Button
          size="sm"
          class="self-start"
          :label="t('POWER_AGENTS.TEST_LINK.SAVE')"
          :is-loading="salvando"
          @click="pedirSalvar"
        />
      </div>

      <!-- Gasto do dia, contadores e quem testou. -->
      <div
        class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TEST_LINK.SPEND') }}
          </p>
          <p v-if="gasto.error" class="mt-0.5 text-xs text-n-amber-11">
            {{ textoDoErro(gasto.error) }}
          </p>
          <p v-else class="mt-0.5 text-sm text-n-slate-11">
            {{
              t('POWER_AGENTS.TEST_LINK.SPEND_VALUE', {
                gasto: dinheiro(gasto.gasto_hoje_usd),
                limite: dinheiro(gasto.limite_usd),
              })
            }}
          </p>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <div
            v-for="contador in contadores"
            :key="contador.id"
            :data-contador="contador.id"
            class="flex flex-col px-3 py-2 border rounded-lg border-n-weak bg-n-background"
          >
            <span class="text-lg font-semibold text-n-slate-12">
              {{ contador.valor }}
            </span>
            <span class="text-xs text-n-slate-10">{{ contador.rotulo }}</span>
          </div>
        </div>
        <p v-if="contagemParcial" class="text-xs text-n-slate-10">
          {{
            t('POWER_AGENTS.TEST_LINK.COUNT_PARTIAL', {
              n: sessoes.sessoes.length,
            })
          }}
        </p>

        <div class="flex flex-col gap-2">
          <p
            class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
          >
            {{ t('POWER_AGENTS.TEST_LINK.SESSIONS') }}
          </p>
          <p v-if="erroDasSessoes" class="text-xs text-n-ruby-11">
            {{ erroDasSessoes }}
          </p>
          <p
            v-else-if="!sessoes.sessoes.length"
            class="text-xs text-n-slate-10"
          >
            {{ t('POWER_AGENTS.TEST_LINK.SESSIONS_EMPTY') }}
          </p>
          <div
            v-for="sessao in sessoes.sessoes"
            :key="sessao.id"
            class="border rounded-lg border-n-weak bg-n-background"
          >
            <!-- No celular quem entrou fica numa linha e os números na de baixo: na mesma
                 linha, o e-mail era cortado para caber a data. -->
            <button
              type="button"
              :data-sessao="sessao.id"
              class="flex items-start w-full gap-2 px-3 py-2 text-left"
              @click="alternarSessao(sessao)"
            >
              <i
                class="mt-0.5 text-sm shrink-0 text-n-slate-10"
                :class="
                  sessaoAberta === sessao.id
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
              />
              <span
                class="flex flex-col flex-1 min-w-0 gap-0.5 sm:flex-row sm:items-center sm:gap-3"
              >
                <span class="text-sm truncate sm:flex-1 text-n-slate-12">
                  {{ quemEntrou(sessao) }}
                </span>
                <span class="flex flex-wrap text-xs gap-x-3 text-n-slate-10">
                  <span>
                    {{
                      t(
                        'POWER_AGENTS.TEST_LINK.SESSION_MESSAGES',
                        { n: sessao.mensagens },
                        sessao.mensagens
                      )
                    }}
                  </span>
                  <span v-if="sessao.visto_em">
                    {{ quando(sessao.visto_em) }}
                  </span>
                </span>
              </span>
            </button>

            <div
              v-if="sessaoAberta === sessao.id"
              class="flex flex-col gap-3 px-3 py-3 border-t border-n-weak"
            >
              <p v-if="carregandoTurnos" class="text-xs text-n-slate-10">
                {{ t('POWER_AGENTS.TEST_LINK.TURNS_LOADING') }}
              </p>
              <!-- No modo canal a conversa mora na caixa de teste, e não aqui: sem esta frase a
                   sessão pareceria vazia por defeito. -->
              <p v-else-if="!turnos.length" class="text-xs text-n-slate-10">
                {{
                  sessao.conversation_id
                    ? t('POWER_AGENTS.TEST_LINK.TURNS_CHANNEL')
                    : t('POWER_AGENTS.TEST_LINK.TURNS_EMPTY')
                }}
              </p>
              <div
                v-for="turno in turnos"
                :key="turno.id"
                class="flex flex-col gap-1"
              >
                <p class="text-xs text-n-slate-10">
                  {{ t('POWER_AGENTS.TEST_LINK.TURN_QUESTION') }}
                </p>
                <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                  {{ turno.pergunta }}
                </p>
                <p
                  v-if="turno.anexos?.length"
                  class="flex items-center gap-1 text-xs text-n-slate-11"
                  data-arquivos-do-turno
                >
                  <i class="shrink-0 i-lucide-paperclip" />
                  {{
                    t('POWER_AGENTS.TEST_LINK.TURN_FILES', {
                      nomes: turno.anexos.map(anexo => anexo.nome).join(', '),
                    })
                  }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ t('POWER_AGENTS.TEST_LINK.TURN_ANSWER') }}
                </p>
                <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                  {{ turno.resposta }}
                </p>
                <RastroDoTurno
                  v-if="turno.rastro?.turn"
                  :turno="cartaoDoRastro(turno)"
                  :nome-do-time="nomeDoTime"
                  data-rastro
                >
                  <p
                    v-if="turno.rastro.funnel?.outcome === 'ok'"
                    class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ t('POWER_AGENTS.TEST_LINK.TRACE_FUNNEL') }}
                  </p>
                </RastroDoTurno>
                <p
                  v-else-if="turno.rastro?.status === 'error'"
                  class="px-2 py-1 text-xs rounded bg-n-ruby-3 text-n-ruby-11"
                  data-rastro-erro
                >
                  {{
                    t('POWER_AGENTS.TEST_LINK.TRACE_ERROR', {
                      tipo: turno.rastro.error_kind || '—',
                    })
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
